import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { useContext, useState } from 'react';
import type { JSX } from 'react';

import AppContext from '@/contexts/appContext';
import Button from '@/ui/components/Button';
import ajax from '@/utils/ajax';

import { AssociateBillingRowsContext } from '../contexts/AssociateBillingRowsContext';
import { BillingReportContext } from '../contexts/BillingReportProvider';
import { BillingReportRow } from '../types';
import { Cms1500 } from '../types/cms_1500';
import { Convert } from '../types/convert';
import { LinkedServiceLine } from '../types/linked_service';
import { ServiceLine } from '../types/service_line';

import ServiceLineItem from './ServiceLineItem';

interface Props {
  rows: BillingReportRow[];
  onDismiss: () => void;
  onClaimLoad: (claim: Cms1500) => void;
  onSaveAssociation: (rows: LinkedServiceLine[]) => void;
  onPendingAssociation: (rows: LinkedServiceLine[]) => void;
}

const AssociateServiceLines = ({
  rows,
  onSaveAssociation,
  onPendingAssociation,
  onClaimLoad,
  onDismiss
}: Props): JSX.Element => {
  // The claim that is currently being inspected and possible having items associated to
  const [cms1500ID, setCms1500ID] = useState('');
  const [claim, setClaim] = useState<Cms1500>();
  const [confirmedClaim, setConfirmedClaim] = useState<boolean>(false);
  const [loading, setLoading] = useState({
    claim: false,
    savingAssociation: false
  });

  const { linkedServiceLines, setLinkedServiceLines } = useContext(AssociateBillingRowsContext);

  const { setErrorMessage } = useContext(BillingReportContext);
  const { host } = useContext(AppContext);

  // Using ajax utility which handles authentication automatically

  const handleAssociatePress = async () => {
    try {
      setLoading({ ...loading, savingAssociation: true });
      await onSaveAssociation(linkedServiceLines);
    } catch (error) {
      console.error('Error saving association:', error);
      setErrorMessage('Failed to save the association');
    } finally {
      setLoading({ ...loading, savingAssociation: false });
      onDismiss();
    }
  };

  const handleCancel = () => {
    if (claim && !confirmedClaim) {
      setClaim(undefined);
    } else {
      setCms1500ID('');
      onDismiss();
    }
  };

  const handleAssociateSelectedItemsButtonPressed = (serviceLine: ServiceLine) => {
    const billingReportRows = rows;

    const serviceIds = billingReportRows.map((row) => row.service_id);

    // Check if this service line is already linked with this billing report row
    if (
      linkedServiceLines.find((linkedServiceLine) =>
        serviceIds.includes(linkedServiceLine.billingReportRow.service_id)
      )
    ) {
      return;
    }

    const newlinkedServiceLine = billingReportRows.map((row) => {
      return {
        billingReportRow: row,
        serviceLine: serviceLine
      } as LinkedServiceLine;
    });

    const newlinkedServiceLines = [...linkedServiceLines, ...newlinkedServiceLine];

    setLinkedServiceLines(newlinkedServiceLines);
    onPendingAssociation(newlinkedServiceLines);
  };

  const loadClaim = async () => {
    setLoading({ ...loading, claim: true });
    try {
      const response = await ajax.get({
        url: `${host}/api/billing/cms1500/by_healthie_id/${cms1500ID}/`
      });

      if (!response.data) {
        return;
      }

      const claim = Convert.toCms1500(JSON.stringify(response.data));

      if (claim) {
        setClaim(claim);
        onClaimLoad(claim);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 404) {
          setErrorMessage(`Claim with Healthie ID ${cms1500ID} not found`);
        } else {
          const errorMessage = `Failed to refresh service line ${error.response?.data?.error}`;

          setErrorMessage(errorMessage);
        }
      } else {
        setErrorMessage(JSON.stringify(error));
        console.error(error);
      }
    } finally {
      setLoading({ ...loading, claim: false });
    }
  };

  return (
    <div className="my-2 max-w-5xl bg-white p-6 rounded shadow">
      <div className="flex justify-between items-center">
        {!claim && <h2 className="text-xl">Enter Healthie Claim ID to Associate</h2>}
        {claim && !confirmedClaim && <h2 className="text-xl">Confirm Claim for Selection</h2>}

        {claim && confirmedClaim && (
          <h2 className="text-xl">Associating items to Claim ID# {cms1500ID}</h2>
        )}

        <div className="flex">
          <Button className="mr-2" onClick={handleCancel} color="grey">
            Cancel
          </Button>
          {claim && !confirmedClaim && (
            <Button
              className="btn btn-green"
              onClick={() => {
                setConfirmedClaim(true);
              }}>
              Select This Claim
            </Button>
          )}
          {claim && confirmedClaim && (
            <Button
              className="btn btn-green flex items-center justify-center"
              onClick={() => {
                handleAssociatePress();
              }}
              disabled={linkedServiceLines.length == 0 || loading.savingAssociation}>
              {loading.savingAssociation ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span className="ml-2">Saving</span>
                </>
              ) : (
                'Save'
              )}
            </Button>
          )}
        </div>
      </div>

      <div className="my-8" />

      {!claim && (
        <div>
          <form>
            <div className="mb-4">
              <label
                htmlFor="cms1500ID"
                className="block text-sm font-medium leading-6 text-gray-900">
                Healthie Claim ID
              </label>
              <div className="mt-2">
                <input
                  type="email"
                  name="cms1500ID"
                  id="cms1500ID"
                  placeholder="Ex. 1234567"
                  className="block w-full rounded-md border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  value={cms1500ID}
                  onChange={(e) => setCms1500ID(e.target.value)}
                />
              </div>
            </div>
            <Button
              type="submit"
              className="btn btn-blue flex items-center justify-center"
              onClick={loadClaim}
              disabled={loading.claim || cms1500ID.length == 0}>
              {loading.claim ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span className="ml-2">Loading Claim</span>
                </>
              ) : (
                'Load Claim'
              )}
            </Button>
          </form>
        </div>
      )}

      {claim && (
        <div className="">
          {!confirmedClaim && (
            <div className="flex-col justify-start items-start gap-8 flex">
              <div className="justify-start items-start gap-16 inline-flex">
                <div className="flex-col justify-center items-start gap-4 inline-flex">
                  <div className="text-black text-base font-semibold">Claim ID</div>
                  <div className="text-black text-base font-normal">{claim.healthieId}</div>
                </div>
                <div className="flex-col justify-center items-start gap-4 inline-flex">
                  <div className="text-black text-base font-semibold">Status of Bill</div>
                  <div className="text-black text-base font-normal">{claim.status}</div>
                </div>
              </div>
              <div className="justify-start items-start gap-16 inline-flex">
                <div className="flex-col justify-center items-start gap-4 inline-flex">
                  <div className="text-black text-base font-semibold">Patient Name</div>
                  <div className="text-black text-base font-normal">{claim.patientFullName}</div>
                </div>
                <div className="flex-col justify-center items-start gap-4 inline-flex">
                  <div className="text-black text-base font-semibold">Birthdate</div>
                  <div className="text-black text-base font-normal">
                    {claim.patientBirthday?.toLocaleDateString()}
                  </div>
                </div>
                <div className="flex-col justify-center items-start gap-4 inline-flex">
                  <div className="text-black text-base font-semibold">Jurisdiction</div>
                  <div className="text-black text-base font-normal">
                    {claim.patientJurisdiction}
                  </div>
                </div>
                <div className="flex-col justify-center items-start gap-4 inline-flex">
                  <div className="text-black text-base font-semibold">Insurance Policy</div>
                  <div className="text-black text-base font-normal">{claim.primaryPlanName}</div>
                </div>
                <div className="flex-col justify-center items-start gap-4 inline-flex">
                  <div className="text-black text-base font-semibold">Rendering Provider</div>
                  <div className="text-black text-base font-normal">
                    {claim.renderingProviderNameWithTitle}
                  </div>
                </div>
              </div>
            </div>
          )}

          <h3 className="my-4 text-black text-base font-semibold">Services</h3>
          <div className="">
            <ul>
              {(claim.cms1500Cptcodes || []).map((row) => (
                <ServiceLineItem
                  key={row.id}
                  row={row}
                  selectedBillingItems={rows}
                  showLinkButton={confirmedClaim}
                  handleLinkButtonPress={handleAssociateSelectedItemsButtonPressed}
                  linkedBillingReportRows={linkedServiceLines
                    .filter((el) => el.serviceLine.id === row.id)
                    .map((el) => el.billingReportRow)}
                />
              ))}
            </ul>
          </div>

          {confirmedClaim && (
            <p className="text-zinc-500 my-2">
              Select items here to associate them with a service above
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AssociateServiceLines;
